/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { INVOICE_STATUS, ROUTES } from "../../constants/constants";
import { FlowType } from "../../store/slices/flowSlice";

// Lines that mean a service was booked — `deposit` is a booking's part-payment line.
// Everything else (product, membership, package, promotion) is a cart item.
const BOOKING_LINE_TYPES = ["booking", "deposit"];

// A failed or cancelled payment can be paid again until the payment timeout releases the
// order. Such an invoice keeps its status, so `can_be_paid` (set on the invoice item GET
// only) is what tells them apart.
export const canRetryPayment = (invoice) =>
  [INVOICE_STATUS.ERROR, INVOICE_STATUS.CANCELLED].includes(invoice?.status) && !!invoice.can_be_paid;

// Where an invoice leads when clicked: the payment page while it can still be settled (a
// paid invoice awaiting confirmation, a failed or a cancelled payment included), otherwise
// the read-only details page — a received payment, a timed-out or a refunded invoice.
export const getInvoiceRoute = (invoice) => {
  const isReceived = invoice.status === INVOICE_STATUS.PAID && invoice.payment_received;
  const isPayable = [
    INVOICE_STATUS.NEW,
    INVOICE_STATUS.PENDING,
    INVOICE_STATUS.PAID,
    INVOICE_STATUS.ERROR,
    INVOICE_STATUS.CANCELLED,
  ].includes(invoice.status);

  return !isReceived && isPayable
    ? ROUTES.INVOICE_PAY.replace(":id", invoice.id)
    : ROUTES.INVOICE_DETAILS.replace(":id", invoice.id);
};

// The status page that reports on an invoice once it is paid, read off the invoice's own
// lines: a client paying an old invoice from the Payments hub has no active flow to read.
// A mixed invoice counts as a booking, since the booking is what needs confirming.
// `activeFlow` only breaks the tie for an invoice that carries no lines at all.
export const getInvoiceStatusRoute = (invoice, activeFlow = null) => {
  const lines = invoice?.lines || [];
  const isBooking = lines.length
    ? lines.some((line) => BOOKING_LINE_TYPES.includes(line.type))
    : activeFlow === FlowType.BOOKING;

  return isBooking ? ROUTES.BOOKING_STATUS : ROUTES.CART_ITEM_STATUS;
};
